import { useState } from 'react';
import {
  AUDIENCE_FIELD_DEFINITIONS,
  AUDIENCE_FIELD_KEYS,
  BRAND_FIELD_DEFINITIONS,
  BRAND_FIELD_KEYS,
  isBrandValueEmpty,
  type BrandFieldKey,
  type BrandProfileView,
  type BrandSourceTag,
  type FieldProvenanceView,
} from '@mg/shared';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { SECTION_COPY } from '../../brand/brandCopy';
import { BRAND_SECTION_IDS } from '../../brand/editPages';
import { AudienceCard, audienceTitle } from '../../brand/fields/AudienceCard';
import { NOT_PROVIDED_TEXT, ReadOnlyField } from '../../brand/fields/ReadOnlyField';
import { EditSection } from '../EditSection';

/** The sections whose fields are only text on the Edit page (spec:174): changed through Brand Setup. */
export type ReadOnlySectionKey = 'companyVoice' | 'brandIdentity' | 'brandVoice' | 'market';

/**
 * A field's source tags as the page shows them: what is stored, and nothing on a field with no content
 * (spec:109). Read-only fields show them too (spec:18; owner question 9).
 */
export function shownProvenance(
  value: unknown,
  stored: FieldProvenanceView | undefined,
): { sources: BrandSourceTag[]; aiSuggested: boolean } {
  if (isBrandValueEmpty(value) || !stored) return { sources: [], aiSuggested: false };
  return { sources: stored.sources, aiSuggested: stored.aiSuggested };
}

const readOnlyKind = (key: BrandFieldKey): 'text' | 'textarea' | 'valuePhilosophy' => {
  const kind = BRAND_FIELD_DEFINITIONS[key].kind;
  return kind === 'valuePhilosophy' ? 'valuePhilosophy' : kind === 'textarea' ? 'textarea' : 'text';
};

/**
 * Company voice and identity, Brand identity, Brand voice or Market as text (spec:174): each field's
 * name, its source tags and its text, or "Not provided"; Value philosophy with its price tier before
 * the option. Changed only through Revisit Brand Setup.
 */
export function ReadOnlySection({ section, view }: { section: ReadOnlySectionKey; view: BrandProfileView }) {
  const copy = SECTION_COPY[section];
  const keys = BRAND_FIELD_KEYS.filter((key) => BRAND_FIELD_DEFINITIONS[key].section === section);
  return (
    <EditSection page="brand" id={BRAND_SECTION_IDS[section]}>
      <Card>
        <CardHeader title={copy.title} description={copy.description} />
        <CardBody className="py-1">
          <dl className="divide-y divide-line-soft">
            {keys.map((key) => {
              const value = view.fields[key];
              const { sources, aiSuggested } = shownProvenance(value, view.provenance[key]);
              return (
                <ReadOnlyField
                  key={key}
                  label={BRAND_FIELD_DEFINITIONS[key].label}
                  value={typeof value === 'string' ? value : null}
                  sources={sources}
                  aiSuggested={aiSuggested}
                  kind={readOnlyKind(key)}
                />
              );
            })}
          </dl>
        </CardBody>
      </Card>
    </EditSection>
  );
}

/**
 * Audiences on the Edit page (spec:175): each one a collapsible card, closed at first, its fields as
 * text. No Add audience and no Remove audience here: those are Brand Setup's (step 7).
 */
export function AudiencesSection({ view }: { view: BrandProfileView }) {
  const copy = SECTION_COPY.audiences;
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const audiences = [...view.audiences].sort((a, b) => a.order - b.order);
  const toggle = (id: string) =>
    setOpen((was) => {
      const next = new Set(was);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <EditSection page="brand" id={BRAND_SECTION_IDS.audiences}>
      <Card>
        <CardHeader title={copy.title} description={copy.description} />
        <CardBody className="space-y-3">
          {audiences.length === 0 ? (
            <p className="text-sm text-ink-faint">{NOT_PROVIDED_TEXT}</p>
          ) : (
            audiences.map((audience, index) => (
              <AudienceCard
                key={audience.id}
                mode="read"
                title={audienceTitle(audience.identity, 'read', index)}
                expanded={open.has(audience.id)}
                onToggle={() => toggle(audience.id)}
              >
                <dl className="-my-3 divide-y divide-line-soft">
                  {AUDIENCE_FIELD_KEYS.map((key) => {
                    const def = AUDIENCE_FIELD_DEFINITIONS[key];
                    const value = audience[key];
                    const { sources, aiSuggested } = shownProvenance(value, audience.provenance[key]);
                    return (
                      <ReadOnlyField
                        key={key}
                        label={def.label}
                        value={value}
                        sources={sources}
                        aiSuggested={aiSuggested}
                        kind={def.kind}
                      />
                    );
                  })}
                </dl>
              </AudienceCard>
            ))
          )}
        </CardBody>
      </Card>
    </EditSection>
  );
}
